import React, { useEffect, useMemo, useState } from 'react'
import {
  Box,
  Button,
  ButtonGroup,
  Card,
  CardBody,
  CardHeader,
  Center,
  HStack,
  Heading,
  Icon,
  SimpleGrid,
  Spinner,
  Stat,
  StatLabel,
  StatNumber,
  Text,
  VStack,
  Wrap,
  WrapItem,
} from '@chakra-ui/react'
import {
  FiCpu,
  FiDownload,
  FiEdit2,
  FiEye,
  FiFile,
  FiFileText,
  FiLayers,
  FiMessageSquare,
  FiSearch,
  FiTag,
  FiUpload,
} from 'react-icons/fi'
import {
  Area,
  AreaChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from 'recharts'
import apiService from '@/services/api'
import { useApiResource } from '@/hooks/useApiResource'
import { useAppTranslation } from '@/contexts/LanguageContext'
import { UsageStatsCard } from '@/components/Common'
import { UsageFeatureKey, UsageSummary, UsageTimeseries } from '@/types'

// Fixed categorical palette — validated for CVD separation and contrast.
// Colors follow the feature (entity), never its position in the chart.
const FEATURE_COLORS: Record<string, string> = {
  chatbot_queries: '#2B6CB0',
  search: '#C05621',
  file_view: '#0987A0',
  file_upload: '#97266D',
  smart_tagging: '#2F855A',
  smart_renaming: '#6B46C1',
}

const CHART_FEATURES: UsageFeatureKey[] = [
  'chatbot_queries',
  'search',
  'file_view',
  'file_upload',
  'smart_tagging',
  'smart_renaming',
]

const DEFAULT_VISIBLE: UsageFeatureKey[] = ['chatbot_queries', 'search', 'file_view']

const QUOTA_CARDS: { key: UsageFeatureKey; icon: React.ElementType; colorScheme: string }[] = [
  { key: 'smart_tagging', icon: FiTag, colorScheme: 'blue' },
  { key: 'smart_renaming', icon: FiEdit2, colorScheme: 'green' },
  { key: 'chatbot_queries', icon: FiMessageSquare, colorScheme: 'purple' },
  { key: 'pages', icon: FiFileText, colorScheme: 'orange' },
  { key: 'files', icon: FiFile, colorScheme: 'teal' },
]

const ACTIVITY_TILES: { key: UsageFeatureKey; icon: React.ElementType }[] = [
  { key: 'search', icon: FiSearch },
  { key: 'file_view', icon: FiEye },
  { key: 'file_upload', icon: FiUpload },
  { key: 'file_download', icon: FiDownload },
  { key: 'ai_summary', icon: FiLayers },
  { key: 'entity_extraction', icon: FiCpu },
]

function daysAgo(days: number): string {
  const d = new Date()
  d.setDate(d.getDate() - days)
  return d.toISOString().split('T')[0]
}

export default function UsageTab() {
  const { t, formatNumber, formatDate } = useAppTranslation('common')
  const [scope, setScope] = useState<'org' | 'user'>('org')
  const [rangeDays, setRangeDays] = useState<30 | 90>(30)
  const [visibleFeatures, setVisibleFeatures] = useState<UsageFeatureKey[]>(DEFAULT_VISIBLE)

  const summary = useApiResource<UsageSummary | null>(
    () => apiService.getUsageSummary(scope),
    null,
    { errorToastTitle: t('profile.usage.errorLoadingData') }
  )

  const timeseries = useApiResource<UsageTimeseries | null>(
    () =>
      apiService.getUsageTimeseries({
        features: CHART_FEATURES,
        from: daysAgo(rangeDays),
        scope,
      }),
    null,
    { errorToastTitle: t('profile.usage.errorLoadingData') }
  )

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => void summary.refresh(), [scope])
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => void timeseries.refresh(), [scope, rangeDays])

  const featureLabel = (key: string) => t(`account.usage.features.${key}`)

  // Merge the per-feature series into recharts rows keyed by date
  const chartData = useMemo(() => {
    if (!timeseries.data) return []

    const rows = new Map<string, Record<string, number | string>>()
    for (const series of timeseries.data.series) {
      for (const point of series.points) {
        const row = rows.get(point.date) ?? { date: point.date }
        row[series.feature] = point.count
        row[`${series.feature}_tokens`] = point.tokens
        rows.set(point.date, row)
      }
    }

    return [...rows.values()].sort((a, b) => String(a.date).localeCompare(String(b.date)))
  }, [timeseries.data])

  const tokensData = useMemo(
    () =>
      chartData.map((row) => ({
        date: row.date,
        tokens: CHART_FEATURES.reduce(
          (sum, feature) => sum + Number(row[`${feature}_tokens`] ?? 0),
          0
        ),
      })),
    [chartData]
  )

  const hasChartData = chartData.length > 0
  const hasTokens = tokensData.some((row) => row.tokens > 0)

  const toggleFeature = (feature: UsageFeatureKey) => {
    setVisibleFeatures((prev) =>
      prev.includes(feature) ? prev.filter((f) => f !== feature) : [...prev, feature]
    )
  }

  const tickFormatter = (value: string) => {
    const date = new Date(`${value}T00:00:00`)
    return Number.isNaN(date.getTime()) ? value : formatDate(date)
  }

  if (summary.loading && !summary.data) {
    return (
      <Center py={16}>
        <Spinner size="xl" color="brand.500" />
      </Center>
    )
  }

  return (
    <VStack spacing={8} align="stretch">
      {/* Filters — one row above everything they affect */}
      <HStack justify="space-between" flexWrap="wrap" gap={3}>
        <ButtonGroup size="sm" isAttached variant="outline">
          <Button
            isActive={scope === 'org'}
            onClick={() => setScope('org')}
            _active={{ bg: 'brand.500', color: 'white', borderColor: 'brand.500' }}
          >
            {t('account.usage.scope.org')}
          </Button>
          <Button
            isActive={scope === 'user'}
            onClick={() => setScope('user')}
            _active={{ bg: 'brand.500', color: 'white', borderColor: 'brand.500' }}
          >
            {t('account.usage.scope.user')}
          </Button>
        </ButtonGroup>

        {summary.data && (
          <Text fontSize="sm" color="gray.500">
            {t('account.usage.period', {
              start: formatDate(new Date(`${summary.data.period.start}T00:00:00`)),
              end: formatDate(new Date(`${summary.data.period.end}T00:00:00`)),
            })}
          </Text>
        )}
      </HStack>

      {/* Plan quota cards */}
      {summary.data && (
        <Box>
          <Heading size="sm" mb={1} color="gray.700">
            {t('account.usage.quotaTitle')}
          </Heading>
          <Text fontSize="sm" color="gray.500" mb={4}>
            {t('account.usage.quotaSubtitle')}
          </Text>
          <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4}>
            {QUOTA_CARDS.map(({ key, icon, colorScheme }) => {
              const stats = summary.data!.features[key]
              if (!stats) return null
              return (
                <UsageStatsCard
                  key={key}
                  title={featureLabel(key)}
                  icon={icon}
                  stats={stats}
                  colorScheme={colorScheme}
                />
              )
            })}
          </SimpleGrid>
        </Box>
      )}

      {/* Activity tiles */}
      {summary.data && (
        <Box>
          <Heading size="sm" mb={1} color="gray.700">
            {t('account.usage.activityTitle')}
          </Heading>
          <Text fontSize="sm" color="gray.500" mb={4}>
            {t('account.usage.activitySubtitle')}
          </Text>
          <SimpleGrid columns={{ base: 2, md: 3, lg: 6 }} spacing={4}>
            {ACTIVITY_TILES.map(({ key, icon }) => {
              const stats = summary.data!.features[key]
              return (
                <Stat
                  key={key}
                  bg="gray.50"
                  p={4}
                  borderRadius="lg"
                  borderWidth="1px"
                  borderColor="gray.200"
                >
                  <StatLabel color="gray.600" fontSize="xs">
                    <HStack spacing={1}>
                      <Icon as={icon} boxSize={3.5} />
                      <Text>{featureLabel(key)}</Text>
                    </HStack>
                  </StatLabel>
                  <StatNumber fontSize="xl" color="gray.800">
                    {formatNumber(stats?.used ?? 0)}
                  </StatNumber>
                </Stat>
              )
            })}
          </SimpleGrid>
        </Box>
      )}

      {/* Activity over time */}
      <Card variant="outline">
        <CardHeader pb={0}>
          <HStack justify="space-between" flexWrap="wrap" gap={3}>
            <Heading size="sm" color="gray.700">
              {t('account.usage.chartTitle')}
            </Heading>
            <ButtonGroup size="xs" isAttached variant="outline">
              <Button
                isActive={rangeDays === 30}
                onClick={() => setRangeDays(30)}
                _active={{ bg: 'brand.500', color: 'white', borderColor: 'brand.500' }}
              >
                {t('account.usage.range.30d')}
              </Button>
              <Button
                isActive={rangeDays === 90}
                onClick={() => setRangeDays(90)}
                _active={{ bg: 'brand.500', color: 'white', borderColor: 'brand.500' }}
              >
                {t('account.usage.range.90d')}
              </Button>
            </ButtonGroup>
          </HStack>
          <Wrap mt={3} spacing={2}>
            {CHART_FEATURES.map((feature) => {
              const isVisible = visibleFeatures.includes(feature)
              return (
                <WrapItem key={feature}>
                  <Button
                    size="xs"
                    variant={isVisible ? 'solid' : 'outline'}
                    onClick={() => toggleFeature(feature)}
                    bg={isVisible ? FEATURE_COLORS[feature] : undefined}
                    color={isVisible ? 'white' : 'gray.600'}
                    borderColor={FEATURE_COLORS[feature]}
                    _hover={{ opacity: 0.85 }}
                    leftIcon={
                      <Box
                        w="8px"
                        h="8px"
                        borderRadius="full"
                        bg={isVisible ? 'white' : FEATURE_COLORS[feature]}
                      />
                    }
                  >
                    {featureLabel(feature)}
                  </Button>
                </WrapItem>
              )
            })}
          </Wrap>
        </CardHeader>
        <CardBody>
          {timeseries.loading && !timeseries.data ? (
            <Center h="300px">
              <Spinner color="brand.500" />
            </Center>
          ) : hasChartData && visibleFeatures.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={chartData} margin={{ top: 8, right: 16, bottom: 0, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                <XAxis
                  dataKey="date"
                  tickFormatter={tickFormatter}
                  tick={{ fontSize: 12, fill: '#718096' }}
                  tickLine={false}
                  axisLine={{ stroke: '#E2E8F0' }}
                />
                <YAxis
                  allowDecimals={false}
                  tick={{ fontSize: 12, fill: '#718096' }}
                  tickLine={false}
                  axisLine={false}
                  width={40}
                />
                <RechartsTooltip
                  labelFormatter={(value) => tickFormatter(String(value))}
                  formatter={(value: number, name: string) => [formatNumber(value), featureLabel(name)]}
                />
                <Legend formatter={(value) => featureLabel(String(value))} />
                {visibleFeatures.map((feature) => (
                  <Line
                    key={feature}
                    type="monotone"
                    dataKey={feature}
                    stroke={FEATURE_COLORS[feature]}
                    strokeWidth={2}
                    dot={false}
                    activeDot={{ r: 4 }}
                    connectNulls
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <Center h="300px">
              <Text color="gray.500" fontSize="sm">
                {t('account.usage.noData')}
              </Text>
            </Center>
          )}
        </CardBody>
      </Card>

      {/* AI token usage */}
      {hasTokens && (
        <Card variant="outline">
          <CardHeader pb={0}>
            <Heading size="sm" color="gray.700">
              {t('account.usage.tokensChartTitle')}
            </Heading>
          </CardHeader>
          <CardBody>
            <ResponsiveContainer width="100%" height={220}>
              <AreaChart data={tokensData} margin={{ top: 8, right: 16, bottom: 0, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                <XAxis
                  dataKey="date"
                  tickFormatter={tickFormatter}
                  tick={{ fontSize: 12, fill: '#718096' }}
                  tickLine={false}
                  axisLine={{ stroke: '#E2E8F0' }}
                />
                <YAxis
                  tick={{ fontSize: 12, fill: '#718096' }}
                  tickLine={false}
                  axisLine={false}
                  width={56}
                  tickFormatter={(value: number) => formatNumber(value)}
                />
                <RechartsTooltip
                  labelFormatter={(value) => tickFormatter(String(value))}
                  formatter={(value: number) => [formatNumber(value), t('account.usage.tokens')]}
                />
                <Area
                  type="monotone"
                  dataKey="tokens"
                  stroke="#2B6CB0"
                  strokeWidth={2}
                  fill="#2B6CB0"
                  fillOpacity={0.15}
                />
              </AreaChart>
            </ResponsiveContainer>
          </CardBody>
        </Card>
      )}
    </VStack>
  )
}
